import { ReactElement, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { InspectionOnTableView } from 'presentation/inspection/inspectionOnView';
import { InspectionColumnsFactory } from './columns';
import { useProject } from 'store/project';
import { useProcessesObject } from 'store/process';
import { useAppNavigation } from 'contexts/navigation';
import { ISO6391LanguageCode } from 'shared/types/locale';
import { withProcessesData } from 'components/common/withProcessesData';
import { useInspectionDataGridSlotProps } from './useInspectionDataGridSlotProps';
import { InspectionEmpty } from '../InspectionEmpty';
import { sxStyles } from './styles';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  INITIAL_SORT_MODEL,
} from './constants';
import { DataGridInstanceName } from 'services/storage/keys';
import { DataGrid } from 'components/common/dataGrid';
import { createClientDataGridConfig } from 'components/common/dataGrid/config/DataGridConfig';
import { useDataGridUrlPagination } from 'components/common/dataGrid/hooks/useDataGridUrlPagination';
import { getInitialStateFromStorage } from 'components/common/dataGrid/utils';

type InspectionDataGridProps = {
  data: InspectionOnTableView[];
  loading?: boolean;
  searchPhrase?: string;
};

function _InspectionDataGrid({
  data,
  loading = false,
  searchPhrase = '',
}: InspectionDataGridProps): ReactElement {
  const intl = useIntl();
  const {
    timezone,
    organizationId,
    _id: projectId,
  } = useProject();
  const processes = useProcessesObject();

  const columns = useMemo(
    () =>
      new InspectionColumnsFactory(
        intl,
        timezone,
        organizationId,
        processes,
        intl.locale as ISO6391LanguageCode
      ).create(),
    [intl, timezone, organizationId, processes]
  );

  const { goToInspectionDetails } = useAppNavigation();
  const slotProps = useInspectionDataGridSlotProps({ columns });

  const { paginationModel, onPaginationModelChange } =
    useDataGridUrlPagination({
      defaultPageSize: DEFAULT_PAGE_SIZE,
    });

  const { filterModel: initialFilterModel } = useMemo(
    () => getInitialStateFromStorage(DataGridInstanceName.inspection),
    []
  );

  const config = useMemo(
    () =>
      createClientDataGridConfig<InspectionOnTableView>(
        DataGridInstanceName.inspection,
        INITIAL_SORT_MODEL,
        initialFilterModel
      )
        .withPagination({
          defaultPageSize: DEFAULT_PAGE_SIZE,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        })
        .withRowDoubleClick((params) => {
          goToInspectionDetails(projectId, params.row._id);
        })
        .withNoRowsOverlay(() => (
          <InspectionEmpty searchPhrase={searchPhrase} />
        ))
        .withSlotProps(slotProps)
        .withAutosizing({ expand: true })
        .build(),
    [goToInspectionDetails, projectId, slotProps, searchPhrase, initialFilterModel]
  );

  return (
    <DataGrid
      data={data}
      columns={columns}
      config={config}
      loading={loading}
      styles={sxStyles}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
    />
  );
}

export const InspectionDataGrid = withProcessesData(_InspectionDataGrid);
